import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { cbtAPI, assetAPI } from '../../api';
import MyTextEditor from './MyTextEditor';

const CbtQuestionCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cbt_data_id = searchParams.get('cbt_data_id');
  
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [question, setQuestion] = useState('');
  const [score, setScore] = useState(1);
  const [max_options_select, setMaxOptionsSelect] = useState(1);
  const [questionType, setQuestionType] = useState('multiple_choice'); // matches backend
  const [questionStatus, setQuestionStatus] = useState('draft');
  const [displaySort, setDisplaySort] = useState(true);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [db_img_files, setDbImgFiles] = useState([]);
  const [cbtConfig, setCbtConfig] = useState({ default_score: 1, max_options_select: 1 });

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  useEffect(() => {
    if (!cbt_data_id) {
      toast.error('CBT ID is missing');
      navigate('/cbt_creator/cbts');
      return;
    }
    fetchCbtDetails();
    fetchImages();
  }, [cbt_data_id]);

  const fetchCbtDetails = async () => {
    try {
      const response = await cbtAPI.getCBTDetails(cbt_data_id);
      if (response.data) {
        setCbtConfig({
          default_score: response.data.default_score || 1,
          max_options_select: response.data.max_options_select || 1
        });
        setScore(response.data.default_score || 1);
        setMaxOptionsSelect(response.data.max_options_select || 1);
      }
    } catch (error) {
      console.error('Failed to fetch CBT details:', error);
      toast.error('Failed to load CBT details');
    }
  };

  const fetchImages = async () => {
    try {
      const response = await assetAPI.getImageLibrary();
      setDbImgFiles(response.data || []);
    } catch (error) {
      toast.error('Failed to load image library');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) {
      toast.error('Question content cannot be empty');
      return;
    }

    // Validation for True/False
    if (questionType === 'true_false' && !correctAnswer) {
      toast.error('Please select the correct answer (True/False)');
      return;
    }
    if (questionType === 'fill_blank' && !correctAnswer.trim()) {
      toast.error('Please enter the correct answer for the blank');
      return;
    }

    setSubmitting(true);
    const questionData = {
      cbt_data_id: cbt_data_id,
      question: question,
      question_type: questionType,
      score: parseFloat(score),
      status: questionStatus,
      display_sort: displaySort,
      options: [],
      correct_answer: correctAnswer,
      explanation: ''
    };

    try {
      const response = await cbtAPI.createQuestion(cbt_data_id, questionData);
      if (response.success || response.data) {
        toast.success('Question created successfully!');
        navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbt_data_id}`);
      } else {
        toast.error(response.error || 'Failed to create question');
      }
    } catch (error) {
      console.error('Create question error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create question';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center gap-4">
            <button
              onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbt_data_id}`)}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Questions
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
              <h1 className="text-2xl font-bold text-white">Create New Question</h1>
              <p className="text-blue-100 text-sm mt-1">CBT ID: {cbt_data_id}</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Question Content *</label>
                  <MyTextEditor
                    initialContent={question}
                    onChange={setQuestion}
                    imageLibrary={db_img_files}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Question Type</label>
                    <select
                      value={questionType}
                      onChange={(e) => {
                        setQuestionType(e.target.value);
                        setCorrectAnswer('');
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="essay">Essay / Theory</option>
                      <option value="true_false">True / False</option>
                      <option value="fill_blank">Fill in the Blank</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Question Status</label>
                    <select
                      value={questionStatus}
                      onChange={(e) => setQuestionStatus(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Score <span className="text-sm text-gray-500">(Default: {cbtConfig.default_score})</span>
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={score}
                      onChange={(e) => setScore(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Max Options Select <span className="text-sm text-gray-500">(Default: {cbtConfig.max_options_select})</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={max_options_select}
                      onChange={(e) => setMaxOptionsSelect(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Display Order</label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={displaySort === true}
                          onChange={() => setDisplaySort(true)}
                          className="h-4 w-4 text-blue-600"
                        />
                        Sequential
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={displaySort === false}
                          onChange={() => setDisplaySort(false)}
                          className="h-4 w-4 text-blue-600"
                        />
                        Random
                      </label>
                    </div>
                  </div>
                </div>

                {/* Conditional correct answer inputs */}
                {(questionType === 'true_false' || questionType === 'fill_blank') && (
                  <div className="border-t pt-4">
                    <label className="block text-gray-700 font-medium mb-2">
                      {questionType === 'true_false' ? 'Correct Answer' : 'Correct Answer (Fill in the Blank)'}
                    </label>
                    {questionType === 'true_false' ? (
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="trueFalse"
                            value="true"
                            checked={correctAnswer === 'true'}
                            onChange={(e) => setCorrectAnswer(e.target.value)}
                            className="h-4 w-4 text-blue-600"
                          />
                          True
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="trueFalse"
                            value="false"
                            checked={correctAnswer === 'false'}
                            onChange={(e) => setCorrectAnswer(e.target.value)}
                            className="h-4 w-4 text-blue-600"
                          />
                          False
                        </label>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={correctAnswer}
                        onChange={(e) => setCorrectAnswer(e.target.value)}
                        placeholder="e.g., Paris"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbt_data_id}`)}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>}
                  Create Question
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CbtQuestionCreate;